import { motion } from 'framer-motion';
import { Flame, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { EXPERIENCE_IMAGES } from '../data/business';

export function ExperienceSection() {
  const { t, isRTL } = useLanguage();

  return (
    <section 
      id="experience" 
      className="py-24 sm:py-32 bg-white"
      data-testid="experience-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className={isRTL ? 'lg:order-2' : ''}
          >
            <h2 
              className={`font-cormorant text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#6B1C23] mb-8 leading-tight ${
                isRTL ? 'font-noto-serif-arabic' : ''
              }`}
              data-testid="experience-title"
            >
              {t('experience.title')}
            </h2>
            
            <p className={`font-manrope text-[#4A4A4A] text-lg mb-10 leading-relaxed ${isRTL ? 'font-cairo' : ''}`}>
              {t('experience.description')}
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#6B1C23]/10 rounded-sm">
                  <Flame className="w-6 h-6 text-[#6B1C23]" />
                </div>
                <div>
                  <p className={`font-manrope text-[#333] font-medium ${isRTL ? 'font-cairo' : ''}`}>
                    {t('experience.tradition')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#6B1C23]/10 rounded-sm">
                  <Heart className="w-6 h-6 text-[#6B1C23]" />
                </div>
                <div>
                  <p className={`font-manrope text-[#333] font-medium ${isRTL ? 'font-cairo' : ''}`}>
                    {t('experience.quality')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Two Stacked Images */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className={`relative ${isRTL ? 'lg:order-1' : ''}`}
          >
            <div className="grid grid-cols-1 gap-4">
              <motion.div 
                className="relative overflow-hidden rounded-sm shadow-xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src={EXPERIENCE_IMAGES[0]}
                  alt="Salam Grill Interior - Dining Area"
                  className="w-full h-[260px] object-cover"
                  data-testid="experience-image-1"
                />
              </motion.div>
              <motion.div 
                className="relative overflow-hidden rounded-sm shadow-xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src={EXPERIENCE_IMAGES[1]}
                  alt="Salam Grill Interior - Restaurant View"
                  className="w-full h-[260px] object-cover"
                  data-testid="experience-image-2"
                />
              </motion.div>
            </div>
            {/* Decorative Border */}
            <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#6B1C23] rounded-sm -z-10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
